import moment from "moment";

// Projeção equiretangular (lat/long -> metros N/L relativos a um centro fixo).
// Erro desprezível na escala de um pivô central (raio típico < ~1km) — não vale
// a complexidade de uma projeção geodésica mais precisa pra esse caso de uso.
const EARTH_RADIUS_M = 6371000;

function toRad(deg) {
  return (deg * Math.PI) / 180;
}

export function latLonToMeters(lat, lon, centerLat, centerLon) {
  const north = toRad(lat - centerLat) * EARTH_RADIUS_M;
  const east = toRad(lon - centerLon) * EARTH_RADIUS_M * Math.cos(toRad(centerLat));
  return { north, east };
}

// Ângulo em convenção de bússola (0° = Norte, 90° = Leste), igual ao "Posição:"
// que o firmware já reporta pro PIR — mantém as duas fontes comparáveis.
export function bearing(north, east) {
  const deg = (Math.atan2(east, north) * 180) / Math.PI;
  return (deg + 360) % 360;
}

export function distance(north, east) {
  return Math.sqrt(north * north + east * east);
}

// O firmware grava Latitude/Longitude como linhas separadas (um TYPE cada) no
// mesmo instante — combina os pares por TIME (já vem como timestamp em ms via
// getTimestamp() do charts.js, então o match por igualdade é exato) e projeta
// em metros relativos ao centro do pivô (CAD_DIR.LATITUDE/LONGITUDE).
export function buildTrajectoryPoints(latSeries, lonSeries, centerLat, centerLon) {
  const lonByTime = new Map(lonSeries.map(([t, v]) => [t, v]));
  const points = [];
  for (const [t, lat] of latSeries) {
    if (!lonByTime.has(t)) continue;
    const lon = Number(lonByTime.get(t));
    const latNum = Number(lat);
    const { north, east } = latLonToMeters(latNum, lon, centerLat, centerLon);
    points.push({
      time: t,
      lat: latNum,
      lon,
      north,
      east,
      distance: distance(north, east),
      angle: bearing(north, east),
    });
  }
  points.sort((a, b) => a.time - b.time);
  return points;
}

export function maxRadius(points) {
  return points.reduce((max, p) => Math.max(max, p.distance), 0);
}

// Posição (SPG) e pressão (sensor PIR) são sensores diferentes, amostrados de
// forma independente — não dá pra casar por igualdade de horário como lat/lon
// em buildTrajectoryPoints(). Em vez disso, cada ponto recebe a leitura de
// pressão mais recente até aquele instante (sorted + ponteiro avançando, já
// que points já vem ordenado por tempo). Sem pressure_setpoint histórico
// disponível, usa o mesmo piso fixo do fw_cir (pir_is_pressurized) como
// aproximação — suficiente pra colorir a trilha, não é um valor de precisão.
const PRESSURIZED_FLOOR_BAR = 0.3;

export function attachPressure(points, pressureSeries) {
  if (!points.length) return points;
  if (!pressureSeries.length) {
    return points.map((p) => ({ ...p, pressure: null, pressurized: false }));
  }
  const sorted = [...pressureSeries].sort((a, b) => a[0] - b[0]);
  let i = 0;
  return points.map((p) => {
    while (i < sorted.length - 1 && sorted[i + 1][0] <= p.time) i++;
    const pressure = sorted[i][0] <= p.time ? Number(sorted[i][1]) : null;
    return { ...p, pressure, pressurized: pressure !== null && pressure >= PRESSURIZED_FLOOR_BAR };
  });
}

export function groupByDay(points) {
  const groups = new Map();
  for (const p of points) {
    const day = moment(p.time).format("YYYY-MM-DD");
    if (!groups.has(day)) groups.set(day, []);
    groups.get(day).push(p);
  }
  // Mais recente primeiro — mesma ordem que os seletores "Dia:" do mockup de referência.
  return new Map([...groups.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1)));
}

// Soma dos deltas angulares consecutivos, cada um normalizado pra (-180°, 180°]
// antes de somar — evita que uma volta 359°->1° seja contada como -358°.
export function netRotation(dayPoints) {
  let net = 0;
  for (let i = 1; i < dayPoints.length; i++) {
    let delta = dayPoints[i].angle - dayPoints[i - 1].angle;
    delta = (((delta + 180) % 360) + 360) % 360 - 180;
    net += delta;
  }
  return net;
}

// Soma a duração de cada intervalo entre pontos consecutivos onde os dois
// extremos vieram pressurizados — mesmo critério usado pra colorir o trecho
// da trilha, então a estatística bate com o que aparece desenhado.
function pressurizedMin(dayPoints) {
  let ms = 0;
  for (let i = 1; i < dayPoints.length; i++) {
    if (dayPoints[i - 1].pressurized && dayPoints[i].pressurized) {
      ms += dayPoints[i].time - dayPoints[i - 1].time;
    }
  }
  return Math.round(ms / 60000);
}

// Classifica cada grau (0-359, convenção bússola) do círculo do pivô em
// 'wet' (passou pressurizado, acima do piso de pressurizado?), 'dry' (passou
// sem pressão) ou 'none' (nunca visitado nesse dia) — usado pra pintar a área
// da circunferência no mapa polar (issue #437: distinguir área molhada, seca
// e não percorrida). 'wet' nunca regride pra 'dry'/'none' se o mesmo grau foi
// visitado mais de uma vez no dia (ex: pivô foi e voltou pelo mesmo trecho).
const SECTOR_RANK = { none: 0, dry: 1, wet: 2 };

export function sectorStates(dayPoints) {
  const states = new Array(360).fill("none");
  const mark = (deg, state) => {
    const d = ((Math.round(deg) % 360) + 360) % 360;
    if (SECTOR_RANK[state] > SECTOR_RANK[states[d]]) states[d] = state;
  };
  for (let i = 1; i < dayPoints.length; i++) {
    const a = dayPoints[i - 1];
    const b = dayPoints[i];
    // Mesma normalização de menor-caminho usada em netRotation(), pra varrer
    // só o arco de fato percorrido entre os dois pontos (não a volta longa).
    let delta = b.angle - a.angle;
    delta = (((delta + 180) % 360) + 360) % 360 - 180;
    const state = a.pressurized && b.pressurized ? "wet" : "dry";
    const steps = Math.max(1, Math.round(Math.abs(delta)));
    const dir = delta >= 0 ? 1 : -1;
    for (let s = 0; s <= steps; s++) mark(a.angle + dir * s, state);
  }
  return states;
}

// Agrupa o array de 360 graus em faixas contíguas do mesmo estado, pra
// desenhar um <path> por faixa em vez de 360 fatias separadas.
export function sectorRuns(states) {
  const runs = [];
  let i = 0;
  while (i < states.length) {
    const state = states[i];
    let j = i;
    while (j + 1 < states.length && states[j + 1] === state) j++;
    runs.push({ start: i, end: j + 1, state });
    i = j + 1;
  }
  if (runs.length > 1) {
    const first = runs[0];
    const last = runs[runs.length - 1];
    if (first.start === 0 && last.end === states.length && first.state === last.state) {
      first.start = last.start - states.length;
      runs.pop();
    }
  }
  return runs;
}

export function dayStats(dayPoints) {
  if (!dayPoints.length) {
    return { netRotation: 0, startAngle: null, endAngle: null, durationMin: 0, pressurizedMin: 0, count: 0 };
  }
  const first = dayPoints[0];
  const last = dayPoints[dayPoints.length - 1];
  return {
    netRotation: netRotation(dayPoints),
    startAngle: first.angle,
    endAngle: last.angle,
    durationMin: moment(last.time).diff(moment(first.time), "minutes"),
    pressurizedMin: pressurizedMin(dayPoints),
    count: dayPoints.length,
  };
}
